import React, { useState, useEffect } from 'react';
import { Database, CheckCircle, Loader2, Table, BarChart as ChartBar, Gauge, Activity, Cpu, LineChart, BarChart2, Network, Workflow, Zap } from 'lucide-react';

interface ProcessingStep {
  id: string;
  title: string;
  status: 'pending' | 'processing' | 'completed';
  progress: number;
}

interface DetectedMetric {
  name: string;
  type: string;
  unit: string;
  range: { min: number; max: number };
}

export const DataProcessingProgress: React.FC = () => {
  const [steps, setSteps] = useState<ProcessingStep[]>([
    {
      id: 'load',
      title: 'Loading Data Files',
      status: 'pending',
      progress: 0
    },
    {
      id: 'validate',
      title: 'Validating Data Structure',
      status: 'pending',
      progress: 0
    },
    {
      id: 'analyze',
      title: 'Analyzing Time Series',
      status: 'pending',
      progress: 0
    },
    {
      id: 'metrics',
      title: 'Identifying Process Metrics',
      status: 'pending',
      progress: 0
    },
    {
      id: 'correlations',
      title: 'Detecting Correlations',
      status: 'pending',
      progress: 0
    },
    {
      id: 'setup',
      title: 'Setting Up Real-time Monitoring',
      status: 'pending',
      progress: 0
    }
  ]);

  const [detectedMetrics, setDetectedMetrics] = useState<DetectedMetric[]>([]);

  useEffect(() => {
    const simulateProgress = async () => {
      // Step 1: Loading Data
      await updateStepProgress('load', 100);
      
      // Step 2: Validation
      setSteps(prev => prev.map(step => 
        step.id === 'validate' ? { ...step, status: 'processing' } : step
      ));
      
      const metrics = [
        { name: 'Flow Rate', type: 'continuous', unit: 'm³/h', range: { min: 0, max: 500 } },
        { name: 'Pressure', type: 'continuous', unit: 'bar', range: { min: 0, max: 16 } },
        { name: 'Temperature', type: 'continuous', unit: '°C', range: { min: 0, max: 100 } },
        { name: 'pH Level', type: 'continuous', unit: 'pH', range: { min: 0, max: 14 } },
        { name: 'Conductivity', type: 'continuous', unit: 'µS/cm', range: { min: 0, max: 2000 } },
        { name: 'Turbidity', type: 'continuous', unit: 'NTU', range: { min: 0, max: 50 } },
        { name: 'Pump Speed', type: 'continuous', unit: 'RPM', range: { min: 0, max: 3000 } },
        { name: 'Tank Level', type: 'continuous', unit: '%', range: { min: 0, max: 100 } }
      ];

      for (let i = 0; i < metrics.length; i++) {
        await new Promise(resolve => setTimeout(resolve, 500));
        setDetectedMetrics(prev => [...prev, metrics[i]]);
        setSteps(prev => prev.map(step =>
          step.id === 'validate' ? { 
            ...step, 
            progress: ((i + 1) / metrics.length) * 100 
          } : step
        ));
      }

      await updateStepProgress('validate', 100);
      
      // Step 3: Time Series Analysis
      await updateStepProgress('analyze', 100);
      
      // Step 4: Process Metrics
      await updateStepProgress('metrics', 100);
      
      // Step 5: Correlations
      await updateStepProgress('correlations', 100);
      
      // Step 6: Setup
      await updateStepProgress('setup', 100);

      // Navigate to dashboard after completion
      setTimeout(() => {
        const event = new CustomEvent('changeSection', { detail: 'dashboard' });
        window.dispatchEvent(event);
      }, 1000);
    };

    simulateProgress();
  }, []);

  const updateStepProgress = async (stepId: string, targetProgress: number) => {
    const step = steps.find(s => s.id === stepId);
    if (!step) return;

    setSteps(prev => prev.map(s =>
      s.id === stepId ? { ...s, status: 'processing' } : s
    ));

    const increment = 5;
    const interval = 50;
    let currentProgress = 0;

    while (currentProgress < targetProgress) {
      await new Promise(resolve => setTimeout(resolve, interval));
      currentProgress = Math.min(currentProgress + increment, targetProgress);
      
      setSteps(prev => prev.map(s =>
        s.id === stepId ? { 
          ...s, 
          progress: currentProgress,
          status: currentProgress === 100 ? 'completed' : 'processing'
        } : s
      ));
    }
  };

  const getStepIcon = (id: string, status: ProcessingStep['status']) => {
    const icons = {
      load: Database,
      validate: Table,
      analyze: LineChart,
      metrics: Gauge,
      correlations: Network,
      setup: Cpu
    };

    const Icon = icons[id as keyof typeof icons] || Activity;

    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'processing':
        return <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />;
      default:
        return <Icon className="h-5 w-5 text-gray-400" />;
    }
  };

  return (
    <div className="max-w-4xl mx-auto mt-8">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-semibold mb-6">Processing System Data</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Progress Steps */}
          <div className="space-y-4">
            {steps.map((step) => (
              <div key={step.id} className="flex items-center space-x-3">
                <div className="flex-shrink-0">{getStepIcon(step.id, step.status)}</div>
                <div className="flex-grow min-w-0">
                  <div className="flex justify-between items-center mb-1">
                    <p className="text-sm font-medium text-gray-900">{step.title}</p>
                    {step.progress !== undefined && (
                      <span className="text-xs text-gray-500">{Math.round(step.progress)}%</span>
                    )}
                  </div>
                  {step.progress !== undefined && (
                    <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-500 rounded-full ${
                          step.status === 'completed' 
                            ? 'bg-green-500' 
                            : step.status === 'error'
                            ? 'bg-red-500'
                            : 'bg-blue-500'
                        }`}
                        style={{ width: `${step.progress}%` }}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Detected Metrics */}
          {detectedMetrics.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Detected Process Metrics</h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                {detectedMetrics.map((metric, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2 bg-white rounded-lg shadow-sm animate-fade-in"
                  >
                    <div className="flex items-center space-x-3">
                      {metric.type === 'continuous' ? (
                        <LineChart className="h-4 w-4 text-blue-500" />
                      ) : (
                        <BarChart2 className="h-4 w-4 text-blue-500" />
                      )}
                      <div>
                        <p className="text-sm font-medium text-gray-900">{metric.name}</p>
                        <p className="text-xs text-gray-500">
                          Range: {metric.range.min} - {metric.range.max} {metric.unit}
                        </p>
                      </div>
                    </div>
                    <span className="text-xs font-medium text-gray-500">{metric.unit}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* System Status */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-blue-900">Data Points</h3>
              <Database className="h-5 w-5 text-blue-500" />
            </div>
            <p className="text-2xl font-bold text-blue-900">124,582</p>
            <p className="text-sm text-blue-600">8 metrics × 15,572 samples</p>
          </div>

          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-green-900">Time Range</h3>
              <Activity className="h-5 w-5 text-green-500" />
            </div>
            <p className="text-2xl font-bold text-green-900">180 days</p>
            <p className="text-sm text-green-600">5-minute intervals</p>
          </div>

          <div className="bg-purple-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-purple-900">Processing Speed</h3>
              <Zap className="h-5 w-5 text-purple-500" />
            </div>
            <p className="text-2xl font-bold text-purple-900">2,450</p>
            <p className="text-sm text-purple-600">samples per second</p>
          </div>
        </div>
      </div>

      <style>
        {`
          @keyframes fade-in {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .animate-fade-in {
            animation: fade-in 0.5s ease-out forwards;
          }
        `}
      </style>
    </div>
  );
};